import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { localizeError } from '../services/errors';
import { ServiceOfferingResponse, ServiceRejection } from '../types';
import { useLanguage } from '../i18n/LanguageContext';
import AddServiceModalV2 from '../components/services/AddServiceModalV2';
import DeleteServiceModal from '../components/services/DeleteServiceModal';
import RejectionReasonsModal from '../components/services/RejectionReasonsModal';
import ServiceFilters, { SortDropdown } from '../components/services/ServiceFilters';
import OrderCell from '../components/services/OrderCell';
import { useAdminActionFeedContext } from '../context/AdminActionFeedContext';

interface ServicesPageProps {
  auth: {
    isAuthenticated: boolean;
    user: { name: string; email: string } | null;
    logout: () => void;
  };
}

function ServicesPage({ auth: _auth }: ServicesPageProps) {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { unreadOfferingIds, clearOffering } = useAdminActionFeedContext();

  const onOfferingRowClick = (offeringId: string) => {
    // The dot persists into the service detail page so the partner gets a
    // second visual cue while they review the change. Click the dot itself
    // (handled below) to dismiss.
    navigate(`/services/${offeringId}`);
  };

  const onDotClick = (e: React.MouseEvent, offeringId: string) => {
    e.stopPropagation();
    clearOffering(offeringId);
  };

  const [offerings, setOfferings] = useState<ServiceOfferingResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Rejections
  const [rejections, setRejections] = useState<ServiceRejection[]>([]);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingService, setEditingService] = useState<ServiceOfferingResponse | null>(null);
  const [deletingService, setDeletingService] = useState<{ id: string; name: string } | null>(null);
  const [showRejections, setShowRejections] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showSort, setShowSort] = useState(false);

  // Search/Filter/Sort
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ categoryId: '', status: '' });
  const [sortBy, setSortBy] = useState('');

  const [reordering, setReordering] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const fetchOfferings = useCallback(async () => {
    if (!auth.token) return;
    try {
      const data = await api.getMyServices(auth.token);
      setOfferings(data);
      setError(null);
    } catch (err) {
      setError(localizeError(err, t, 'services.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [auth.token, t]);

  const fetchRejections = useCallback(async () => {
    if (!auth.token) return;
    try {
      const data = await api.getServiceRejections(auth.token);
      setRejections(data);
    } catch {
      // Silently ignore — may not have rejections endpoint
    }
  }, [auth.token]);

  useEffect(() => {
    fetchOfferings();
    fetchRejections();
  }, [fetchOfferings, fetchRejections]);

  // The garage's services in their saved display order. The API already returns them ordered;
  // sorting again keeps the arrows honest if a response ever arrives out of order.
  const orderedOfferings = useMemo(
    () => [...offerings].sort((a, b) => {
      const ao = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
      const bo = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      // createdAt only breaks ties among offerings that predate the display_order
      // backfill. It is typed as required but can be absent in practice, and this
      // comparator runs on every render — an undefined here takes the page down.
      return (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
    }),
    [offerings]
  );

  // Reordering rewrites every position, so it only makes sense while the table is showing the
  // real order. Under a search, filter or price sort, "move up" has no well-defined meaning —
  // the row above is not the neighbour in the saved order. Pagination is fine: the arrows act
  // on the full ordered list, not the visible page.
  const canReorder = !searchTerm && !filters.categoryId && !filters.status && !sortBy;

  const moveOfferingTo = useCallback(async (offeringId: string, targetPosition: number) => {
    if (!auth.token) return;
    const current = orderedOfferings.map((o) => o.id);
    const from = current.indexOf(offeringId);
    const to = Math.min(Math.max(targetPosition, 1), current.length) - 1;
    if (from < 0 || from === to) return;

    const next = [...current];
    next.splice(to, 0, next.splice(from, 1)[0]);

    setReordering(true);
    // Optimistic: the row should move under the cursor, not after a round trip, or a partner
    // clicking ↑ repeatedly ends up fighting the refresh.
    const byId = new Map(offerings.map((o) => [o.id, o]));
    setOfferings(next.map((id, i) => ({ ...byId.get(id)!, displayOrder: i + 1 })));
    try {
      const saved = await api.reorderServices(next, auth.token);
      setOfferings(saved);
      setError(null);
    } catch (err) {
      setOfferings(offerings); // put it back — the saved order is unchanged
      setError(localizeError(err, t, 'services.reorderFailed'));
    } finally {
      setReordering(false);
    }
  }, [auth.token, offerings, orderedOfferings, t]);

  // Filter + Search + Sort
  const filteredOfferings = useMemo(() => {
    let result = [...orderedOfferings];

    // Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((o) =>
        (o.name || o.category.name).toLowerCase().includes(term) ||
        o.category.name.toLowerCase().includes(term)
      );
    }

    // Filter by category
    if (filters.categoryId) {
      result = result.filter((o) =>
        o.category.id === filters.categoryId || o.category.parentId === filters.categoryId
      );
    }

    // Filter by status
    if (filters.status) {
      result = result.filter((o) => o.status === filters.status);
    }

    // Sort
    if (sortBy === 'price_asc') {
      result.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price_desc') {
      result.sort((a, b) => b.price - a.price);
    }

    return result;
  }, [orderedOfferings, searchTerm, filters, sortBy]);

  // Pagination
  const totalItems = filteredOfferings.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
  const paginatedOfferings = filteredOfferings.slice((page - 1) * perPage, page * perPage);
  const startItem = totalItems > 0 ? (page - 1) * perPage + 1 : 0;
  const endItem = Math.min(page * perPage, totalItems);

  // Duration displayed as days + hours (never minutes)
  const formatDuration = (minutes: number) => {
    const days = Math.floor(minutes / (24 * 60));
    const hours = Math.floor((minutes % (24 * 60)) / 60);
    if (days > 0 && hours > 0) return `${days} days ${hours} hours`;
    if (days > 0) return `${days} days`;
    if (hours > 0) return `${hours} hours`;
    return '0 hours';
  };

  const getStatusBadge = (offering: ServiceOfferingResponse) => {
    if (offering.blockedAt) {
      return (
        <span
          className="partner-status-badge"
          style={{ background: '#fde2e2', color: '#7f1d1d', border: '1px solid #7f1d1d' }}
          title="Blocked by Garagat admin. Contact support to unblock."
        >
          BLOCKED
        </span>
      );
    }
    switch (offering.status) {
      case 'ACTIVE':
        return (
          <span className="partner-status-badge active">
            <svg className="partner-status-badge-icon" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M2 6l3 3 5-5" />
            </svg>
            {t('services.status.active')}
          </span>
        );
      case 'INACTIVE':
        return (
          <span className="partner-status-badge inactive">
            <svg className="partner-status-badge-icon" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M2 6l3 3 5-5" />
            </svg>
            {t('services.status.inactive')}
          </span>
        );
      case 'PENDING_APPROVAL':
        return <span className="partner-status-badge pending">{t('services.status.pending')}</span>;
      default:
        return <span className="partner-status-badge">{offering.status}</span>;
    }
  };

  // Resolve Category (parent) and Subcategory (leaf) for the table.
  // Backend exposes `parentName` on the offering's category when it is a subcategory.
  const getCategoryName = (o: ServiceOfferingResponse) => {
    if (o.category.parentId) {
      return o.category.parentName || '-';
    }
    return o.category.name;
  };

  const getSubcategoryName = (o: ServiceOfferingResponse) => {
    if (o.category.parentId) {
      return o.category.name;
    }
    return '-';
  };

  if (loading) {
    return (
      <div className="partner-services-page">
        <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>{t('services.loadingServices')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="partner-services-page">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  // Empty state
  if (offerings.length === 0) {
    return (
      <div className="partner-services-page">
        <div className="partner-services-empty">
          <div className="partner-services-empty-illustration">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="8" y="12" width="48" height="40" rx="4" stroke="#1e293b" strokeWidth="3" fill="none" />
              <line x1="8" y1="24" x2="56" y2="24" stroke="#1e293b" strokeWidth="3" />
              <line x1="28" y1="24" x2="28" y2="52" stroke="#1e293b" strokeWidth="2" />
              <circle cx="18" cy="18" r="2" fill="#e5c526" />
              <circle cx="24" cy="18" r="2" fill="#e5c526" />
              <circle cx="30" cy="18" r="2" fill="#e5c526" />
              <rect x="34" y="30" width="16" height="16" rx="2" stroke="#e5c526" strokeWidth="2" fill="none" />
              <line x1="42" y1="34" x2="42" y2="42" stroke="#e5c526" strokeWidth="2" strokeLinecap="round" />
              <line x1="38" y1="38" x2="46" y2="38" stroke="#e5c526" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <h3>{t('buildServices.heading')}</h3>
          <p>{t('buildServices.subtext')}</p>
          <button className="partner-services-empty-btn" onClick={() => setShowAddModal(true)}>
            {t('buildServices.addServices')}
          </button>
        </div>

        {showAddModal && auth.token && (
          <AddServiceModalV2
            token={auth.token}
            onClose={() => setShowAddModal(false)}
            onSuccess={fetchOfferings}
          />
        )}
      </div>
    );
  }

  return (
    <div className="partner-services-page">
      {/* Header */}
      <div className="partner-services-header">
        <div className="partner-services-header-left">
          <h2>{t('services.title')}</h2>
          <p>{t('services.subtitle')}</p>
        </div>
        <button className="partner-services-add-btn" onClick={() => setShowAddModal(true)}>
          + {t('services.addNewService')}
        </button>
      </div>

      {/* Rejection Banner */}
      {rejections.length > 0 && (
        <div className="partner-rejection-banner">
          <span>{t('services.rejectionBanner')}</span>
          <button className="partner-rejection-banner-link" onClick={() => setShowRejections(true)}>
            {t('services.rejectionView')}
          </button>
        </div>
      )}

      {/* Search + Filter + Sort */}
      <div className="partner-services-toolbar">
        <div className="partner-services-search">
          <svg className="partner-services-search-icon" width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="8" cy="8" r="5" />
            <path d="M15 15l-3.5-3.5" />
          </svg>
          <input
            type="text"
            placeholder={t('common.search')}
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
          />
        </div>
        <button className="partner-services-filter-btn" onClick={() => setShowFilters(true)} title={t('services.filters.title')}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M2 3h14" />
            <path d="M5 9h8" />
            <path d="M7 15h4" />
          </svg>
        </button>
        <button className="partner-services-sort-btn" onClick={() => setShowSort(true)} title={t('services.sort.title')}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 6l5-4 5 4" />
            <path d="M4 12l5 4 5-4" />
          </svg>
        </button>
      </div>

      {/* Table */}
      <div className="partner-services-table-wrapper">
        <table className="partner-services-table">
          <thead>
            <tr>
              <th className="partner-services-order-col">{t('services.table.order')}</th>
              <th>{t('services.table.name')}</th>
              <th>{t('services.table.price')}</th>
              <th>{t('services.table.duration')}</th>
              <th>{t('services.table.category')}</th>
              <th>{t('services.table.subcategory')}</th>
              <th>{t('services.table.status')}</th>
              <th>{t('services.table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {paginatedOfferings.map((offering, rowIndex) => (
              <tr key={offering.id}>
                <td className="partner-services-order-col">
                  <OrderCell
                    position={offering.displayOrder ?? (rowIndex + 1)}
                    total={orderedOfferings.length}
                    disabled={!canReorder || reordering}
                    disabledHint={canReorder ? undefined : t('services.reorderDisabledHint')}
                    onMove={(to) => moveOfferingTo(offering.id, to)}
                  />
                </td>
                <td>
                  <span
                    className="partner-services-name-link"
                    onClick={() => onOfferingRowClick(offering.id)}
                  >
                    {offering.name || offering.category.name}
                    {unreadOfferingIds.has(offering.id) && (
                      <span
                        className="partner-services-update-dot"
                        title="New admin update — click to dismiss"
                        role="button"
                        tabIndex={0}
                        onClick={(e) => onDotClick(e, offering.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                            clearOffering(offering.id);
                          }
                        }}
                      />
                    )}
                  </span>
                </td>
                <td>AED {offering.price.toFixed(2)}</td>
                <td>{formatDuration(offering.durationMinutes)}</td>
                <td>{getCategoryName(offering)}</td>
                <td>{getSubcategoryName(offering)}</td>
                <td>{getStatusBadge(offering)}</td>
                <td>
                  <div className="partner-table-actions">
                    <button
                      className="partner-table-action-btn"
                      title={offering.blockedAt ? 'Blocked by admin — contact support' : t('common.edit')}
                      disabled={!!offering.blockedAt}
                      onClick={() => !offering.blockedAt && setEditingService(offering)}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11.3 1.7a1.5 1.5 0 0 1 2.1 0l.9.9a1.5 1.5 0 0 1 0 2.1L5.5 13.5 1 15l1.5-4.5L11.3 1.7z" />
                      </svg>
                    </button>
                    <button
                      className="partner-table-action-btn delete"
                      title={offering.blockedAt ? 'Blocked by admin — contact support' : t('common.remove')}
                      disabled={!!offering.blockedAt}
                      onClick={() => !offering.blockedAt && setDeletingService({ id: offering.id, name: offering.name || offering.category.name })}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="2 4 3.5 4 14 4" />
                        <path d="M12.5 4v9a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 13V4m2 0V2.5A1.5 1.5 0 0 1 7 1h2a1.5 1.5 0 0 1 1.5 1.5V4" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="partner-pagination">
          <div className="partner-pagination-left">
            <span>{t('services.pagination.resultsPerPage')}</span>
            <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
          <div className="partner-pagination-right">
            <span className="partner-pagination-info">
              {startItem}-{endItem} {t('services.pagination.from')} {totalItems}
            </span>
            <button className="partner-pagination-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 3l-4 4 4 4" /></svg>
            </button>
            <button className="partner-pagination-btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 3l4 4-4 4" /></svg>
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && auth.token && (
        <AddServiceModalV2
          token={auth.token}
          onClose={() => setShowAddModal(false)}
          onSuccess={fetchOfferings}
        />
      )}

      {editingService && auth.token && (
        <AddServiceModalV2
          token={auth.token}
          editOfferingId={editingService.id}
          onClose={() => setEditingService(null)}
          onSuccess={fetchOfferings}
        />
      )}

      {deletingService && auth.token && (
        <DeleteServiceModal
          token={auth.token}
          serviceId={deletingService.id}
          serviceName={deletingService.name}
          onClose={() => setDeletingService(null)}
          onSuccess={fetchOfferings}
        />
      )}

      {showRejections && (
        <RejectionReasonsModal
          rejections={rejections}
          onClose={() => setShowRejections(false)}
        />
      )}

      {showFilters && (
        <ServiceFilters
          initialFilters={filters}
          onApply={(f) => { setFilters(f); setPage(1); }}
          onClose={() => setShowFilters(false)}
        />
      )}

      {showSort && (
        <SortDropdown
          value={sortBy}
          onChange={(v) => { setSortBy(v); setPage(1); }}
          onClose={() => setShowSort(false)}
        />
      )}
    </div>
  );
}

export default ServicesPage;
